import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  RefreshCw,
  Trash,
} from "lucide-react";
import { useState } from "react";
import type { Blueprint } from "@/services/blueprints-service";
import { getBlueprintApiErrorMessage } from "@/services/blueprints-service";
import { toast } from "react-toastify";
import { useBlueprintsTableData } from "../hooks/useBlueprintsTableData";
import { useDeleteBlueprint } from "../hooks/useBlueprintMutations";
import DeleteBlueprint from "./DeleteBlueprint";
import BlueprintDetailsDialog from "./BlueprintDetailsDialog";
import { useTranslation } from "react-i18next";

interface BlueprintTableProps {
  searchQuery: string;
  projectIds?: string[];
}

const BlueprintTable = ({ searchQuery, projectIds }: BlueprintTableProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { blueprints, metadata, isLoading, isFetching, refetch } =
    useBlueprintsTableData({
      search: searchQuery || null,
      page,
      pageSize: rowsPerPage,
      projectIds,
    });

  const pageCount = Math.max(1, metadata?.total_pages ?? 1);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(
    null,
  );
  const [blueprintToDelete, setBlueprintToDelete] = useState<Blueprint | null>(
    null,
  );
  const deleteBlueprint = useDeleteBlueprint();

  const handleEditClick = (bluePrint: Blueprint) => {
    setSelectedBlueprint(bluePrint);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (blueprint: Blueprint) => {
    setBlueprintToDelete(blueprint);
  };
  const handleDeleteConfirm = async () => {
    if (!blueprintToDelete) return;

    try {
      await deleteBlueprint.mutateAsync(blueprintToDelete.id);
      setBlueprintToDelete(null);
    } catch (error) {
      toast.error(
        getBlueprintApiErrorMessage(error, "Failed to delete blueprint"),
      );
    }
  };
  return (
    <section className="overflow-hidden">
      <div className="flex items-center justify-between py-3">
        <h2 className="text-xs font-medium text-foreground">
          {t("blueprints.allBlueprints")}
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={t("common.refresh", "Refresh")}
          aria-label={t("common.refresh", "Refresh")}
          disabled={isFetching}
          className="size-7 text-muted-foreground hover:text-foreground"
          onClick={() => void refetch()}
        >
          <RefreshCw
            className={`size-3.5 ${isFetching ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      <div className="flex flex-col gap-1.5 pb-3">
        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("blueprints.loadingBlueprints")}
          </p>
        ) : blueprints.length ? (
          blueprints.map((blueprint) => {
            const isEditable = blueprint.safety_status === "active";

            return (
              <Card
                key={blueprint.id}
                className={`min-h-[58px] rounded-[6px] border-border bg-card py-0 shadow-none ${
                  isEditable ? "" : "opacity-50"
                }`}
              >
                <CardContent className="grid min-h-[58px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-3 py-2">
                  {/* Blueprint information */}
                  <div className="min-w-0">
                    <h3 className="truncate text-xs font-medium text-foreground">
                      {blueprint.name}
                    </h3>

                    <p className="truncate text-[0.65rem] text-muted-foreground">
                      {blueprint.description}
                    </p>
                  </div>

                  {/* Creator + Actions */}
                  <div className="flex items-center gap-5">
                    <div className="hidden min-w-[150px] flex-col sm:flex">
                      <div className="flex gap-2 items-center">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground text-xs font-medium text-sidebar-primary-foreground">
                          {blueprint?.username?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                        <span className="text-xs text-foreground">
                          {blueprint?.username || "Unknown user"}
                        </span>
                      </div>

                      <span className="text-[0.65rem] text-muted-foreground">
                        {new Date(blueprint.created_at).toLocaleString(
                          undefined,
                          {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>

                    <div className="hidden min-w-[82px] flex-col sm:flex">
                      <span className="text-[0.6rem] text-muted-foreground">
                        {t("common.status")}
                      </span>
                      <span
                        className={`text-xs font-medium capitalize ${
                          blueprint.safety_status === "active"
                            ? "text-emerald-600"
                            : blueprint.safety_status === "failed"
                              ? "text-red-600"
                              : "text-amber-600"
                        }`}
                      >
                        {t(`common.${blueprint.safety_status}`)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={t("common.edit")}
                        aria-label={`Edit ${blueprint.name}`}
                        // disabled={!isEditable}
                        className="size-7 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => handleEditClick(blueprint)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={t("common.delete")}
                        aria-label={`Delete ${blueprint.name}`}
                        className="size-7 text-muted-foreground hover:text-foreground"
                        onClick={() => handleDeleteClick(blueprint)}
                      >
                        <Trash className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("blueprints.noBlueprints")}
          </p>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between py-2.5">
        {/* Rows per page */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("blueprints.rowsPerPage")}</span>

          <Select
            value={String(rowsPerPage)}
            onValueChange={(value) => {
              setRowsPerPage(Number(value));
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 min-w-[64px] text-xs">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {[10, 25, 50].map((size) => (
                <SelectItem key={size} value={String(size)} className="text-xs">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((current) => current - 1)}
            className="gap-0.5 px-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
            <span>{t("blueprints.previous")}</span>
          </Button>

          {Array.from({ length: pageCount }, (_, index) => index + 1).map(
            (pageNumber) => (
              <Button
                key={pageNumber}
                type="button"
                variant={page === pageNumber ? "outline" : "ghost"}
                size="icon"
                onClick={() => setPage(pageNumber)}
                className={`size-8 rounded-lg text-xs ${
                  page === pageNumber
                    ? "bg-muted font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-muted/50"
                }`}
              >
                {pageNumber}
              </Button>
            ),
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={page === pageCount}
            onClick={() => setPage((current) => current + 1)}
            className="gap-0.5 px-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <span>{t("blueprints.next")}</span>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {/* Edit Blueprint Dialog */}
      {selectedBlueprint && (
        <BlueprintDetailsDialog
          open={isEditOpen}
          blueprint={{ id: selectedBlueprint.id }}
          onOpenChange={(open) => {
            setIsEditOpen(open);
            if (!open) {
              setSelectedBlueprint(null);
            }
          }}
        />
      )}

      <DeleteBlueprint
        blueprint={blueprintToDelete}
        isDeleting={deleteBlueprint.isPending}
        onCancel={() => setBlueprintToDelete(null)}
        onConfirm={() => void handleDeleteConfirm()}
      />
    </section>
  );
};

export default BlueprintTable;
